/**
 * Which presenter a device gets (MYD-498).
 *
 *   desktop  the full-screen presenter with page-flip and the keyboard
 *   tablet   the touch viewer: swipe, a filmstrip, controls sized for a finger
 *   phone    nothing: a phone is told to open the deck on a tablet or computer
 *
 * "Touch" means the primary pointer is coarse AND the device cannot hover: an
 * iPad with a trackpad attached reports hover, and is better served by the
 * desktop presenter, which takes swipes as well.
 *
 * Phone versus tablet is decided by the SHORT side of the screen, not the
 * viewport, so rotating never turns a tablet into a phone. Every current phone
 * is under 600px on its short side (the largest, about 440); the smallest
 * iPad, 744.
 *
 * `?viewer=desktop|tablet|phone` overrides the guess, for checking a layout on
 * hardware the guess gets wrong.
 */
export type ViewerKind = 'desktop' | 'tablet' | 'phone'

export const PHONE_MAX_SHORT_SIDE = 600

const OVERRIDES: ViewerKind[] = ['desktop', 'tablet', 'phone']

export const viewerKind = (override?: string | null, win: Window | undefined = typeof window === 'undefined' ? undefined : window): ViewerKind => {
	if (override && (OVERRIDES as string[]).includes(override)) return override as ViewerKind
	if (!win?.matchMedia) return 'desktop'
	const touch = win.matchMedia('(pointer: coarse)').matches && win.matchMedia('(hover: none)').matches
	if (!touch) return 'desktop'
	const shortSide = Math.min(win.screen?.width ?? Infinity, win.screen?.height ?? Infinity)
	return shortSide < PHONE_MAX_SHORT_SIDE ? 'phone' : 'tablet'
}
